import { createRef, useEffect, useRef, useState } from 'react';

import { parseDate } from '@datepicker-react/hooks';
import moment from 'moment';

import { DrawerProps } from '@components/Drawer/next';
import { CloseIcon, PersonIcon, PlusIcon } from '@assets/icons';
import {
  KidInterface,
  ListingSearchRoomInterface,
} from '@components/ui/search/SearchGuest/ListingSearchGuestInterface';
import CustomIcon from '@components/utils/CustomIcon';
import {
  FORM_DATE_FORMAT,
  getAgeFromFormattedString,
  getCurrentDate,
  isAfterDateTime,
} from '@utils/dateUtil';

import ResponsiveListingRoom from './ResponsiveListingRoom';
import {
  ResponsiveGuestDrawerContent,
  ResponsiveGuestRooms,
  ResponsiveGuestRoomsList,
  ResponsiveGuestRoomsNew,
  ResponsiveGuestRoomsNewText,
  ResponsiveSearchDrawerButton,
  ResponsiveSearchSubmitButtonWrapper,
  StyledListingGuestDrawer,
  StyledRoomsWrapper,
} from './ResponsiveListingSearchStyle';
import ResponsiveSearchDrawerItem from './ResponsiveSearchDrawerItem';

export const validRoomChildAge = (date: string, maxAge = 15) => {
  let isValid = true;

  const ageFromYears = getAgeFromFormattedString(date, FORM_DATE_FORMAT);

  const notValidAge = isAfterDateTime(
    moment(parseDate(date, String(`dd/MM/yyyy`), new Date())),
    moment(
      parseDate(
        getCurrentDate().format('DD/MM/YYYY'),
        String(`dd/MM/yyyy`),
        new Date()
      )
    )
  );

  !date || String(date) === '' || ageFromYears > maxAge || notValidAge
    ? (isValid = false)
    : (isValid = true);

  return isValid;
};

export const GuestListing = ({
  onHotelRoomsUpdate,
  hotelRooms,
  maxAdultCount = 6,
}: {
  onHotelRoomsUpdate: (hotelRooms: Array<ListingSearchRoomInterface>) => void;
  hotelRooms: Array<ListingSearchRoomInterface>;
  maxAdultCount?: number;
}) => {
  const roomGuestsWrapperRef = useRef<HTMLDivElement>(null);
  const [newKid, setNewKid] = useState<KidInterface>();
  const [roomId, setRoomId] = useState<string>();

  // ==========================================================================
  // -Custom functions to control number of adult in each room
  // -using this function we can increase the number of adult guest in each room
  // ==========================================================================
  const handleIncrementAdultNumber = (id: string): void => {
    const newRooms = hotelRooms?.map((room) =>
      room.id === id && room.adultCount < maxAdultCount
        ? { ...room, adultCount: room.adultCount + 1 }
        : room
    );
    onHotelRoomsUpdate(newRooms);
  };

  // ==========================================================================
  // -Custom functions to control number of adult in each room
  // -using this function we can decrease the number of adult guest in each room
  // ==========================================================================
  const handleDecrementAdultNumber = (id: string): void => {
    const newRooms = hotelRooms?.map((room) =>
      room.id === id ? { ...room, adultCount: room.adultCount - 1 } : room
    );
    onHotelRoomsUpdate(newRooms);
  };

  // ============================================================
  // -Custom functions to control number of kids in each room
  // -using this function we can add new kid in each room
  // =============================================================
  const addNewChild = (roomId: string, newKid: KidInterface): void => {
    const newRooms = hotelRooms?.map((room) =>
      room.id === roomId && room.childrenList?.length < 3
        ? { ...room, childrenList: [...room.childrenList, newKid] }
        : room
    );

    onHotelRoomsUpdate(newRooms);
    setNewKid(newKid);
    setRoomId(roomId);
  };

  useEffect(() => {
    const room = hotelRooms?.find((item) => item.id === roomId);

    if (room) {
      roomGuestsWrapperRef.current?.scrollTo({
        top:
          room.childrenList.length === 3
            ? room.ref.current.offsetTop + 220
            : room.ref.current.offsetTop + 130,
        left: 100,
        behavior: 'smooth',
      });
    }
  }, [newKid]);

  // ============================================================
  // -Custom functions to control number of kids in each room
  // -using this function we can remove a kid from each room
  // =============================================================
  const removeChild = (roomId: string, kidId: string): void => {
    const newRooms = hotelRooms?.map((room) =>
      room.id === roomId
        ? {
            ...room,
            childrenList: [
              ...room.childrenList.slice(0, Number(kidId)),
              ...room.childrenList.slice(Number(kidId) + 1),
            ],
          }
        : room
    );
    onHotelRoomsUpdate(newRooms);
  };

  // ============================================================
  // -Custom functions to control the age of kids in each room
  // -using this function we can change the kid's age
  // =============================================================
  const changeChildAge = (kidId: string, age: string): void => {
    if (!kidId && !age) return;
    const newRooms = hotelRooms?.map((room) =>
      room.childrenList.length !== 0
        ? {
            ...room,
            childrenList:
              room.childrenList &&
              room.childrenList?.map((kid) =>
                kid.id === kidId
                  ? {
                      ...kid,
                      age: age,
                      error: validRoomChildAge(age) ? false : true,
                    }
                  : kid
              ),
          }
        : room
    );
    onHotelRoomsUpdate(newRooms);
  };

  // ============================================================
  // -Custom functions to control the rooms
  // -using this function we can remove a room
  // =============================================================
  const removeRoom = (roomId: string): void => {
    const newRooms = hotelRooms.filter((room) => room.id !== roomId);
    onHotelRoomsUpdate(newRooms);
  };

  return (
    <StyledRoomsWrapper ref={roomGuestsWrapperRef}>
      {hotelRooms.length > 0 &&
        hotelRooms?.map((room, index) => (
          <div ref={room.ref} key={room.key}>
            <ResponsiveListingRoom
              elementRef={room.ref}
              title={`${index + 1}. Oda`}
              rooms={hotelRooms}
              key={room.key}
              closable={hotelRooms.length === 1 ? false : room.closable}
              id={room.id}
              adultCount={room.adultCount}
              min={1}
              error={room.error}
              adultGuestCount={room.adultCount}
              childGuestCount={room.childrenList.length}
              childrenList={room.childrenList}
              handleAdultNumberDecrement={() =>
                handleDecrementAdultNumber(room.id)
              }
              handleAdultNumberIncrement={() =>
                handleIncrementAdultNumber(room.id)
              }
              handleChildNumberDecrement={() =>
                removeChild(room.id, (room.childrenList.length - 1).toString())
              }
              handleChildNumberIncrement={() =>
                addNewChild(room.id, {
                  id: Date.now().toString(),
                  age: '',
                  error: false,
                  ref: createRef<HTMLDivElement>(),
                })
              }
              changeChildAge={changeChildAge}
              removeRoom={() => removeRoom(room.id)}
            />
          </div>
        ))}
    </StyledRoomsWrapper>
  );
};

interface ResponsiveListingSearchGuestProps {
  showGuestModal: () => void;
  hideGuestModal: () => void;
  rooms: Array<ListingSearchRoomInterface>;
  onHotelRoomsUpdate: (hotelRooms: Array<ListingSearchRoomInterface>) => void;
  handleChildError: () => void;
  clearRoomsValues?: () => void;
  finialRooms: Array<ListingSearchRoomInterface>;
  searchText?: string;
  handleUpdateFinalRooms: (rooms: Array<ListingSearchRoomInterface>) => void;
  addNewRoom?: () => void;
  maxAdultCount?: number;
}
const ResponsiveListingSearchGuest = ({
  hideGuestModal,
  showGuestModal,
  rooms: hotelRooms,
  handleChildError,
  addNewRoom,
  handleUpdateFinalRooms,
  clearRoomsValues,
  onHotelRoomsUpdate,
  finialRooms,
  visible,
  searchText = 'Kaç Kişi Konaklayacaksınız?',
  disableAddRoomButton,
  maxAdultCount,
  ...otherProps
}: React.PropsWithChildren<
  DrawerProps &
    ResponsiveListingSearchGuestProps & {
      layout?: number;
      disableAddRoomButton?: boolean;
    }
>) => {
  // ===================================
  // Validate the rooms every time a change happens
  // ===================================
  useRef(hotelRooms?.map((item) => (item.ref = createRef<HTMLDivElement>())));
  const handleApplyClickEvent = (): void => {
    const regex = /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/;

    let isError = false;
    hotelRooms?.map((room) =>
      room.childrenList.length !== 0
        ? {
            ...room,
            childrenList:
              room.childrenList &&
              room.childrenList.find((kid) => {
                validRoomChildAge(String(kid.age))
                  ? (isError = false)
                  : (isError = true);
              }),
          }
        : room
    );

    const errorRoom = hotelRooms.find((room) =>
      room.childrenList.find(
        (child) =>
          (child && child.age === '') ||
          String(child.age).length < 10 ||
          regex.test(String(child.age)) === false
      )
    );

    if (errorRoom) {
      errorRoom.ref.current?.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }

    if (isError) return handleChildError();

    handleUpdateFinalRooms(hotelRooms);
    hideGuestModal();
  };

  const adultNumber = finialRooms.reduce((t, a) => t + a.adultCount, 0);
  const childrenCount = finialRooms.reduce(
    (t, a) => t + a.childrenList.length,
    0
  );
  const roomSelectBoxLabel = `${
    finialRooms.length
  } oda, ${adultNumber} yetişkin ${
    childrenCount > 0 ? ', ' + childrenCount + ' çocuk' : ''
  }`;

  return (
    <>
      <ResponsiveSearchDrawerItem
        title={searchText}
        subTitle={roomSelectBoxLabel}
        icon={PersonIcon}
        arrowIcon={true}
        onClick={showGuestModal}
        className='border-bottom-hidden'
        isFilled={adultNumber > 0 || childrenCount > 0}
      />
      {visible ? (
        <StyledListingGuestDrawer
          title='Kişi - Oda Sayısı Seçimi'
          closeIcon={
            <CustomIcon icon={CloseIcon} fontSize={24} paletteColor='black' />
          }
          onClose={() => {
            clearRoomsValues?.();
            hideGuestModal();
          }}
          visible={visible}
          {...otherProps}
        >
          <ResponsiveGuestDrawerContent>
            <ResponsiveGuestRooms>
              <ResponsiveGuestRoomsList>
                <GuestListing
                  onHotelRoomsUpdate={onHotelRoomsUpdate}
                  hotelRooms={hotelRooms}
                  maxAdultCount={maxAdultCount}
                />
              </ResponsiveGuestRoomsList>
            </ResponsiveGuestRooms>
            {disableAddRoomButton ? null : (
              <ResponsiveGuestRoomsNew
                disabled={hotelRooms.length === 6 ? true : false}
                onClick={addNewRoom}
              >
                <ResponsiveGuestRoomsNewText>
                  Oda Ekle
                </ResponsiveGuestRoomsNewText>
                <CustomIcon icon={PlusIcon} fontSize={20} />
              </ResponsiveGuestRoomsNew>
            )}
            <ResponsiveSearchSubmitButtonWrapper>
              <ResponsiveSearchDrawerButton
                variant='Primary'
                block
                onClick={handleApplyClickEvent}
              >
                Uygula
              </ResponsiveSearchDrawerButton>
            </ResponsiveSearchSubmitButtonWrapper>
          </ResponsiveGuestDrawerContent>
        </StyledListingGuestDrawer>
      ) : null}
    </>
  );
};

export default ResponsiveListingSearchGuest;
